import { useState } from 'react';
import { Alert, Stack, Typography } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { AuthLayout, SubmitButton } from '../components';
import { getApiScopes } from '../auth/msalConfig';

/**
 * The blocked / failed sign-in reasons (Prompt A3). All five exist as design
 * states; the whoami-401 path currently wires only `generic` because the
 * backend collapses every denial to an indistinguishable bare 401 (TD-191).
 */
export type BlockedReason =
  | 'organizationNotRecognized'
  | 'accountDeactivated'
  | 'organizationInactive'
  | 'signInFailed'
  | 'generic';

interface SignInBlockedPageProps {
  reason: BlockedReason;
}

/**
 * Dead-end "we couldn't sign you in" card (Prompt A3). PUBLIC, neutral brand,
 * outside the shell. One primary action:
 *
 *  - `signInFailed` (MSAL cancelled/timed out) → "Try again" re-initiates
 *    `loginRedirect`. The user still has no session, so nothing to clear.
 *  - every other reason is a genuine denial for an account that *does* hold a
 *    valid MSAL session → "Back to sign in" runs `logoutRedirect` to clear that
 *    session. Navigating alone would loop: `RequireAuth` would re-admit the
 *    account and whoami would 401 again (slice 02 goal, loop-safety note).
 */
export function SignInBlockedPage({ reason }: SignInBlockedPageProps) {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const isRetry = reason === 'signInFailed';

  // The primary action hands off to an MSAL redirect (which normally tears the
  // page down). If the redirect never starts (e.g. `postLogoutRedirectUri` not
  // registered, or an interaction already in progress), recover the button so
  // the user isn't stranded on a permanently-spinning dead-end.
  //
  // The `signInFailed` flag is deliberately NOT cleared here. This card is
  // rendered by `RequireAuth` as well as by `SignInPage`, and clearing it first
  // would re-render the guard into its redirecting state, unmount this card
  // mid-click, and lose the failure Alert on the very path that needs it. There
  // is nothing to clear afterwards either: `loginRedirect` never fulfils (it
  // navigates away, and its promise only ever rejects, on timeout), so the flag
  // is cleared by the page load the redirect triggers. Keeping it set until then
  // means a redirect that never starts leaves the user on a card that still
  // explains why.
  const handleAction = async () => {
    setPending(true);
    setFailed(false);
    try {
      if (isRetry) {
        await instance.loginRedirect({ scopes: getApiScopes() });
      } else {
        await instance.logoutRedirect();
      }
    } catch {
      setPending(false);
      setFailed(true);
    }
  };

  return (
    <AuthLayout footer={<Typography variant="body2">{t('signIn.footerSupport')}</Typography>}>
      <Stack spacing={2} sx={{ alignItems: 'center', width: '100%' }}>
        <Typography variant="h6" component="h1">
          {t(`signInBlocked.${reason}.title`)}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t(`signInBlocked.${reason}.body`)}
        </Typography>
        {failed && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {t('errors.generic')}
          </Alert>
        )}
        <SubmitButton type="button" fullWidth pending={pending} onClick={() => void handleAction()}>
          {isRetry ? t('actions.tryAgain') : t('signInBlocked.backToSignIn')}
        </SubmitButton>
      </Stack>
    </AuthLayout>
  );
}
